import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  Output,
} from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms'

import chroma from 'chroma-js'

export const COLOR_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ColorPickerComponent),
  multi: true,
}

@Component({
  selector: 'color-picker',
  templateUrl: 'color-picker.component.html',
  styleUrls: ['color-picker.component.less'],
  providers: [
    COLOR_PICKER_VALUE_ACCESSOR
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColorPickerComponent implements ControlValueAccessor {
  @Input() value: string = ''
  @Input() themeColors
  @Input() settings
  @Output() valueChanged = new EventEmitter()
  @HostBinding('class') classes = 'ui labeled icon input'

  @HostListener('document:click', ['$event']) clickedOutside(event) {
    let path = event.path || (event.composedPath && event.composedPath())
    if (this.active && !path.includes(this.elementRef.nativeElement)) {
      this.active = false
    }
  }

  defaultSettings = {
    greyscale: true,
    themeColors: true,
    themeAlternatives: true,
    closeOnSelection: true,
    transparent: false,
    custom: false
  }

  greyScale: string[]
  darkThemeColors: string[]
  lightThemeColors: string[]
  active: boolean = false
  disabled: boolean = false

  constructor(private elementRef: ElementRef, private changeRef: ChangeDetectorRef) {
    this.greyScale = chroma.scale(['#FFFFF8', '#A6A6A0', '040400']).colors(10)
      .add(chroma.scale(['#FFFFFF', '#A2A2A2', '#000000']).colors(10))
      .add(chroma.scale(['#FAFCFF', '#A2A6AB', '#00060F']).colors(10))
      .map(c => c.toUpperCase())
  }

  ngOnInit() {
    this.settings = Object.assign(this.defaultSettings, this.settings)
    this.themeColors && this.themeColors.sortBy(color => chroma(color).hsl()[0])
    if (this.themeColors && this.settings.themeAlternatives) {
      this.lightThemeColors = this.themeColors.map(color => {
        return chroma(color).brighten(1).desaturate(0.25).hex()
      })
      this.darkThemeColors = this.themeColors.map(color => {
        return chroma(color).darken(1.5).saturate(0.5).hex()
      })
    }
  }

  onChangeCallback: (value: string) => void
  onTouchedChangeCallback: () => void

  writeValue(value) {
    this.value = value
    this.changeRef.detectChanges()
  }

  selectedColor(value?) {
    if (!this.disabled) {
      this.value = value
      if (typeof this.onChangeCallback === 'function') {
        this.onChangeCallback(this.value)
      }
      if (this.settings.closeOnSelection) {
        this.active = false
      }
    }
  }

  open() {
    if (!this.disabled) {
      this.active = true
      this.onTouchedChangeCallback()
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled
    if (this.disabled) {
      this.classes = this.classes.split(' ').add('disabled').join(' ')
    } else {
      this.classes = this.classes.split(' ').remove('disabled').join(' ')
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn
  }

  registerOnTouched(fn: any) {
    this.onTouchedChangeCallback = fn
  }
}